import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getMessaging, isSupported } from 'firebase/messaging';
import { getFunctions } from 'firebase/functions';
import { getAI, GoogleAIBackend } from 'firebase/ai';
import { getAnalytics, initializeAnalytics, isSupported as isAnalyticsSupported } from 'firebase/analytics';
import { analyticsLocation } from '../utils/analyticsUrl';

// Environment-based Firebase configuration
const getFirebaseConfig = () => {
  // Development/Staging configuration (your current config)
  const stagingConfig = {
    apiKey: "AIzaSyCZmcUm3VKdmBixKES4Gip3GeKefYk9GA8",
    authDomain: "peak-x-staging.firebaseapp.com",
    projectId: "peak-x-staging",
    storageBucket: "peak-x-staging.firebasestorage.app",
    messagingSenderId: "710805219697",
    appId: "1:710805219697:web:835bf45ac524611f6eaed0",
    measurementId: "G-G31Q27H79T"
  };
  

  // Production configuration (from environment variables)
  const productionConfig = {
    apiKey: "AIzaSyD_bal8834MIFxoc05zfWh7aI5K7uR_0oQ",
    authDomain: "peak-x-937b6.firebaseapp.com",
    databaseURL: "https://peak-x-937b6-default-rtdb.asia-southeast1.firebasedatabase.app",
    projectId: "peak-x-937b6",
    storageBucket: "peak-x-937b6.firebasestorage.app",
    messagingSenderId: "377620642931",
    appId: "1:377620642931:web:704b27d79e552b6fe53e06",
    measurementId: "G-Y8DNP0FGD8"
  };

  const isProduction = process.env.REACT_APP_FORCE_PRODUCT_MODE === 'true' || (typeof window !== 'undefined' && (window.location.hostname === 'peakxv2.web.app' || window.location.hostname === 'peak.app' || window.location.hostname === 'www.peak.app'))
  const config = isProduction ? productionConfig : stagingConfig;

  // Validate required fields
  if (!config.apiKey || !config.projectId) {
    throw new Error('Firebase configuration is missing required fields. Please check your environment variables.');
  }

  console.log(`🔥 Firebase initialized for ${isProduction ? 'PRODUCTION' : 'STAGING'} environment`);
  console.log(`📦 Project ID: ${config.projectId}`);
  
  return config;
};

const firebaseConfig = getFirebaseConfig();

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app);

// Initialize Firestore and get references to the services
export const db = getFirestore(app, 'version2');
export const defaultDb = getFirestore(app, '(default)'); // default database for Terra data

// Initialize Firebase Storage and get a reference to the service
export const storage = getStorage(app);

// Initialize Firebase Functions and get a reference to the service
export const functions = getFunctions(app, 'us-central1');

// Separate AI instance backed by the Gemini Developer API. Required for models
// that are not yet hosted on Vertex AI (e.g. `gemini-3.1-flash-live-preview`).
export const aiGoogle = getAI(app, { backend: new GoogleAIBackend() });

// Initialize Firebase Messaging conditionally (only if supported)
let messaging: ReturnType<typeof getMessaging> | null = null;
isSupported().then((supported) => {
  if (supported) {
    messaging = getMessaging(app);
  }
});

// Initialize Firebase Analytics conditionally (only if supported)
let analytics: ReturnType<typeof getAnalytics> | null = null;
isAnalyticsSupported().then((supported) => {
  if (supported) {
    // The automatic page view would report the raw URL, including one-time
    // codes on reset links; App sends cleaned page views instead.
    analytics = initializeAnalytics(app, {
      config: { send_page_view: false, page_location: analyticsLocation() },
    });
    console.log('📊 Google Analytics initialized');
  }
}).catch((error) => {
  console.warn('Analytics not supported:', error);
});

export { messaging, analytics };
export default app; 